import { useNavigate, useParams } from "react-router-dom";
import { useGetResultsetModelFamiliesResultsetModelFamiliesResultsetIdGet, useGetResultsetPredictionsMetadataResultsetPredictionsResultsetIdGet, useGetResultsetsResultsetGet, useGetResultsetV2ResultsetDataframeV2ResultsetIdGet } from "../../../api/generated/fastAPI";
import { useEffect, useMemo, useState, MouseEvent, SyntheticEvent } from "react";
import { Prediction, PredictionEvaluationMetadata, Resultset } from "../../../api/models";
import SinglePageHeader from "../../../components/Page/SinglePageHeader";
import { TableData, TableHeader } from "../../../components/Table/DataTable";
import SelectField from "../../../components/Form/SelectFields";
import { exportFileExtensions } from "../../../utils/supplementalData";
import usePaginate from "../../../hooks/usePaginate";
import useMessage from "../../../hooks/useMessage";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import ListGridToggle from "../../../components/Buttons/ListGridToggle";
import Pagination from "../../../components/Filtering/Pagination";
import SnackBar from "../../../components/Alerts/SnackBar";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";
import { ReactComponent as RecordsIcon } from '../../../assets/Icons/adornments/Records.svg';
import HorizontalNumberSlider from "../../../components/Data/HorizontalNumberSlider";
import ResultComparison from "../components/ResultComparison";
import GenerateReadAcrossModal from "../../ReadAcross/components/GenerateReadAcrossModal";
import SingleCompoundPredictionModal from "../components/SingleCompoundPredictionModal";
import NewPageTabs from "../../../components/Tabs/NewPageTabs";
import ResultsetVisualizations from "../components/ResultsetVisualizations";
import DeleteModal from "../../../components/Popups/DeleteModal";
import useDelete from "../../../hooks/endpoints/useDelete";
import ModelValidations from "../components/ModelValidations";
import ResultsetExplainabilityTab from "../components/ResultsetExplainabilityTab";
import DCMTrustTab from "../components/DCMTrustTab";
import { useAccess } from "../../../access/useAccess";
import { readacross_access } from "../../../access/predefinedConditions";
import useDownload from "../../../hooks/endpoints/useDownload";
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable";
import { formatDisplayedPredictionMetadata, formatDisplayedResultsetMetadata, formatResultsetDataframe, MetadataMenuItemType } from "../../../utils/dataFormattingFns";
import UpdatedMetadataMenu from "../../../components/Menus&Dropdowns/UpdatedMetadataMenu";
import VirtualizedGridList from "../../../components/Grid/VirtualizedGridList";
import EmptyRecords from "../../../components/Alerts/EmptyRecords";
import BooleanField from "../../../components/Form/BooleanField";


export interface ResultsetGrouping {
    modelFamilyId: number,
    modelFamilyName: string,
    isConformal: boolean,
    hasDcm: boolean,
    dcmModelNames: string[],
    dcmModelHashes: string[],
    metricsHeaders: TableHeader[],
    modelHeaders: TableHeader[],
    moleculeHighlightingHeaders: TableHeader[],
    data: TableData[],
    hasProba: boolean,
}

export interface ResultsetEvaluationMetadataWithStatistics extends ResultsetEvaluationMetadata {
    statistics: { [key: string]: any },
}

export interface ResultsetEvaluationMetadata { 
    name: string, 
    predictionId: string, 
    evaluationMetadata?: PredictionEvaluationMetadata,
    modelFamilyId: number, 
}

export interface MoleculeHighlightingType {
    dataframe: {
        atom_bond_importance: {
            [key: number]: any,
        },
        smiles: {
            [key: number]: any,
        },
    },
    metadata: any,
}

export interface IndividualSmilesMoleculeHighlightingType {
    dataframe: {
        atom_bond_importance: any,
        smiles: any,
    },
    metadata: any,
}

const toThresholdOrNull = (value: unknown): number | null => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return null;
    }
    return Math.max(0, Math.min(1, numericValue));
};

const extractExplicitDcmThreshold = (prediction: Prediction): number | null => {
    const modelMetadata = (prediction as any)?.model_metadata ?? {};
    const dcmMetadata = modelMetadata?.dcm_metadata ?? {};
    const dcmParams = dcmMetadata?.job_metadata?.dcm_params ?? {};
    const runtimeMetrics = dcmMetadata?.runtime_metrics ?? {};

    // Convergence threshold is training stability, not inference domain threshold.
    const candidates = [
        dcmParams.domain_threshold,
        dcmParams.inference_domain_threshold,
        dcmParams.threshold,
        runtimeMetrics.domain_threshold,
        runtimeMetrics.inference_domain_threshold,
    ];

    for (const candidate of candidates) {
        const threshold = toThresholdOrNull(candidate);
        if (threshold !== null) {
            return threshold;
        }
    }

    return null;
};

const getDefaultDcmThreshold = (
    grouping: ResultsetGrouping | null,
    predictionItems: Prediction[],
    selectedDcmModelHash?: string | null,
): number => {
    if (!grouping || !grouping.hasDcm || predictionItems.length === 0) return 0.5;

    const dcmModelHashes = grouping.dcmModelHashes ?? [];

    if (dcmModelHashes.length === 0) return 0.5;

    const prioritizedHashes = selectedDcmModelHash
        ? [selectedDcmModelHash, ...dcmModelHashes.filter((hash) => hash !== selectedDcmModelHash)]
        : dcmModelHashes;

    for (const hash of prioritizedHashes) {
        const matchingPrediction = predictionItems.find((prediction) => String(prediction.model_hash ?? '') === hash);
        if (!matchingPrediction) {
            continue;
        }

        const threshold = extractExplicitDcmThreshold(matchingPrediction);
        if (threshold !== null) {
            return threshold;
        }
    }

    return 0.5;
};

export default function IndividualResultset() {
    const { id } = useParams();
    const { downloadBatchPrediction } = useDownload();
    const getResultsetPredictionMetadata = useGetResultsetPredictionsMetadataResultsetPredictionsResultsetIdGet(id ? Number(id) : 0);
    const getResultsetPredictionsByModelFamily = useGetResultsetModelFamiliesResultsetModelFamiliesResultsetIdGet(id ? Number(id) : 0);
    const getResultsets = useGetResultsetsResultsetGet({
        resultset_id: id,
    });

    const getResultsetDataframeV2 = useGetResultsetV2ResultsetDataframeV2ResultsetIdGet(id ? Number(id) : 0, {
        applicability_domain: true,
        include_duplicate_values: true,
        include_duplicates: true,
        include_molecule_highlighting: false,
    });

    const { canAccess } = useAccess();

    const [ resultset, setResultset ] = useState<Resultset | null>(null);
    const [ resultsetDataframeV2, setResultsetDataframeV2 ] = useState<any | null>(null);
    const [ predictions, setPredictions ] = useState<Prediction[]>([]);
    const [ resultsetMetadata, setResultsetMetadata ] = useState<MetadataMenuItemType[]>([]);
    const [ useProba, setUseProba ] = useState<boolean>(false);
    const [ hasProba, setHasProba ] = useState<boolean>(false);

    const [ modelFamilyOptions, setModelFamilyOptions ] = useState<{name: string, id: number}[]>([]);
    const [ chosenModelFamily, setChosenModelFamily ] = useState<{name: string, id: number} | null>(null);
    const [ allPredictionResults, setAllPredictionResults ] = useState<ResultsetGrouping[]>([]);
    
    const navigate = useNavigate();

    const [ headers, setHeaders ] = useState<TableHeader[]>([]);
    const [ predictionResults, setPredictionResults ] = useState<TableData[]>([]);
    const [ evalMetadata, setEvalMetadata ] = useState<ResultsetEvaluationMetadata[]>([]);
    const [ moleculeHighlightingModelFamily, setMoleculeHighlightingModelFamily ] = useState<number | null>(null);
    const [ isGrid, setIsGrid ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isDataLoading, setIsDataLoading ] = useState(false);
    const [ threshold, setThreshold ] = useState<number>(0.5);
    const [ dcmThreshold, setDcmThreshold ] = useState<number>(0.5);
    const [ selectedDcmModelHash, setSelectedDcmModelHash ] = useState<string | null>(null);
    const [ isConformal, setIsConformal ] = useState(false);
    const [ selectedIndex, setSelectedIndex ] = useState(0);
    const [ pageTabs, setPageTabs ] = useState<string[]>([]);

    const modelHashLabels = useMemo(() => {
        const labels: { [key: string]: string } = {};

        // Prefer model header labels, which are method names keyed by model hash.
        allPredictionResults.forEach((group: ResultsetGrouping) => {
            (group.modelHeaders ?? []).forEach((header: TableHeader) => {
                if (!header?.value || !header?.label) {
                    return;
                }
                labels[String(header.value)] = String(header.label);
            });
        });

        // Fallback to prediction names when headers are not yet available.
        predictions.forEach((prediction: Prediction) => {
            const modelHash = prediction.model_hash;
            if (!modelHash || labels[String(modelHash)]) {
                return;
            }
            const displayName = prediction.name ? String(prediction.name) : `Model ${String(modelHash).slice(0, 12)}`;
            labels[String(modelHash)] = displayName;
        });

        return labels;
    }, [allPredictionResults, predictions]);

    const {
        setItems,
        setItemsPerPage,
        setCurrentPage,
        currentPage,
        paginatedItems,
        startIndex,
        endIndex,
        totalPages,
        paginate,
    } = usePaginate();

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const { deleteResultsets } = useDelete({ setPopupMessage });
    

    useEffect(() => {
        if (getResultsets.isLoading 
            || getResultsetDataframeV2.isLoading 
            || getResultsetPredictionMetadata.isLoading 
            || getResultsetPredictionsByModelFamily.isLoading 
            || isDataLoading
        ) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [
        getResultsets.isLoading,
        getResultsetPredictionMetadata.isLoading,
        getResultsetPredictionsByModelFamily.isLoading,
        getResultsetDataframeV2.isLoading,
        isDataLoading,
    ]);

    useEffect(() => {
        // Gets resultset data from url params 
        if (id && getResultsets.data) {
            const data = getResultsets.data as unknown as Resultset;
            
            // Redirect to single prediction page if resultset metadata provides SMILES values
            if (data.resultset_metadata!.smiles) {
                navigate(`/resultsets/compound/${id}`)
            } else {
                setResultset(data)
            }
        }
    }, [id, getResultsets.data, navigate]);


    useEffect(() => {
        // Sets all dataframes contained in resultset
        if (getResultsetDataframeV2.data) {
            const dataframe = getResultsetDataframeV2.data as unknown as Resultset;
            setResultsetDataframeV2(dataframe);
        }
    }, [getResultsetDataframeV2.data]);

    useEffect(() => {
        if (getResultsetPredictionMetadata.data) {
            const data = getResultsetPredictionMetadata.data as unknown as Prediction[];
            setPredictions(data);
        }
    }, [getResultsetPredictionMetadata.data]);

    useEffect(() => {
        const fetchResultsetMetadata = async (rs: Resultset, preds: Prediction[], modelFamilyFiles: { id: number, name: string }[]) => {
            const metadata = await formatDisplayedResultsetMetadata(rs, preds, modelFamilyFiles);
            setResultsetMetadata(metadata);
        };

        if (resultset && allPredictionResults.length > 0) {
            const modelFamilyFiles: { id: number, name: string }[] = allPredictionResults.map(result => {
                return {
                    id: result.modelFamilyId,
                    name: result.modelFamilyName,
                }
            })
            fetchResultsetMetadata(resultset, predictions, modelFamilyFiles);
        }
    }, [resultset, predictions, allPredictionResults]);


    useEffect(() => {
        const getResultsetData = async () => {
            if (predictions.length > 0 && resultsetDataframeV2) {

                setIsDataLoading(true);

                try {
                    
                    const formattedResultsetData = await formatResultsetDataframe(resultsetDataframeV2, predictions, useProba);
                    const { groupedRsData, predEvalMetadata } = formattedResultsetData;

                    const tabs = ['Prediction Scores', 'Explainability', 'Visualizations'];

                    if (groupedRsData.some(group => group.hasDcm && !group.isConformal)) {
                        tabs.splice(1, 0, 'DCM Trust');
                    }

                    if (predEvalMetadata.length > 0) {
                        tabs.splice(1, 0, 'Model Validations');
                    };

                    setPageTabs(tabs);
                    setEvalMetadata(predEvalMetadata);

                    const modelFamilyIdsAndNames: { name: string, id: number }[] = [];

                    const allGroupedData: ResultsetGrouping[] = [];

                    groupedRsData.forEach(rsData => {
                        modelFamilyIdsAndNames.push({
                            name: rsData.modelFamilyName,
                            id: rsData.modelFamilyId,
                        });

                        const modelHeaders = rsData.modelHeaders.map(header => {
                            let columnMetadata;
                            if (predictions.length > 0) {
                                const matchingPrediction = predictions.find(pred => pred.model_hash === header.value);
                                if (matchingPrediction) {
                                    const predMetadata = formatDisplayedPredictionMetadata(matchingPrediction, header.label);
                                    columnMetadata = (
                                        <UpdatedMetadataMenu 
                                            menuLabel="Prediction Metadata"
                                            menuItems={predMetadata}
                                            location="header"
                                        />
                                    );
                                }
                            };
                            return {
                                ...header,
                                columnMetadata,
                            }
                        });

                        const updatedData = rsData.data.map(data => {
                            const options: DropdownOption[] = [];

                            const smilesField = data.fields.find(field => field.type === 'structure');
                            const smiles = smilesField ? smilesField.value as string : '';

                            if (canAccess({or: readacross_access})) options.push(
                                {
                                        label: 'Generate Read-Across',
                                        btnComp: (
                                            <GenerateReadAcrossModal 
                                                setPopupMessage={setPopupMessage}
                                                selectedCompound={smiles}
                                                location="dropdown"
                                            />
                                        )
                                    },
                            );
                            options.push(
                                {
                                        label: 'Run Prediction',
                                        btnComp: (
                                            <SingleCompoundPredictionModal 
                                                setPopupMessage={setPopupMessage}
                                                selectedCompound={smiles}
                                                location="dropdown"
                                            />
                                    )
                                }
                            );

                            return {
                                ...data,
                                options,
                            }
                        });

                        allGroupedData.push({
                            ...rsData,
                            modelHeaders,
                            data: updatedData,
                        })
                    });

                    setModelFamilyOptions(modelFamilyIdsAndNames);
                    // Only set initial model family selection; don't reset on useProba toggle
                    if (!chosenModelFamily || !modelFamilyIdsAndNames.some(mf => mf.id === chosenModelFamily.id)) {
                        setChosenModelFamily(modelFamilyIdsAndNames[0]);
                    }
                    setAllPredictionResults(allGroupedData);
                } catch (err:any) {
                    console.log(err);
                }

                setIsDataLoading(false);
                
            }
        }

        getResultsetData();

        //eslint-disable-next-line
    }, [predictions, resultsetDataframeV2, useProba]);

    useEffect(() => {
        if (chosenModelFamily && allPredictionResults.length > 0) {
            const predictionResults = allPredictionResults.find(result => result.modelFamilyId === chosenModelFamily.id);

            if (predictionResults) {
                const {
                    modelFamilyId,
                    isConformal: isConformalPredictor,
                    metricsHeaders,
                    modelHeaders,
                    moleculeHighlightingHeaders,
                    data,
                    hasProba,
                } = predictionResults;

                setHasProba(hasProba);
                if (!hasProba) { // also set useProba to false if regression
                    setUseProba(false);
                }

                const tableHeaders: TableHeader[] = metricsHeaders.concat(modelHeaders);

                setIsConformal(isConformalPredictor);
                setHeaders(tableHeaders);
                setItems(data);
                setPredictionResults(data);

                if (moleculeHighlightingHeaders.length > 0) {
                    setMoleculeHighlightingModelFamily(modelFamilyId);
                }

            }
        }

        //eslint-disable-next-line
    }, [chosenModelFamily, allPredictionResults]);

    const activeGrouping = useMemo(() => {
        if (!chosenModelFamily) return null;
        return allPredictionResults.find((group) => group.modelFamilyId === chosenModelFamily.id) ?? null;
    }, [chosenModelFamily, allPredictionResults]);

    useEffect(() => {
        if (!activeGrouping || !activeGrouping.hasDcm) {
            setSelectedDcmModelHash(null);
            return;
        }

        const dcmHashes = activeGrouping.dcmModelHashes ?? [];
        if (dcmHashes.length === 0) {
            setSelectedDcmModelHash(null);
            return;
        }

        if (!selectedDcmModelHash || !dcmHashes.includes(selectedDcmModelHash)) {
            setSelectedDcmModelHash(dcmHashes[0]);
        }

    }, [activeGrouping, selectedDcmModelHash]);

    useEffect(() => {
        if (!activeGrouping || !activeGrouping.hasDcm) {
            return;
        }

        const defaultThreshold = getDefaultDcmThreshold(activeGrouping, predictions, selectedDcmModelHash);
        setDcmThreshold(defaultThreshold);
    }, [activeGrouping, predictions, selectedDcmModelHash]);

    
    const handleChooseModelFamily = (e:any, name: string) => {
        const value = e.value;
        const matchingModelFamily = modelFamilyOptions.find(option => option.id === value);
        if (matchingModelFamily) {
            setChosenModelFamily(matchingModelFamily);
        }
    };

    const dropdownOptions: DropdownOption[] = [
        {
            label: 'Export',
            action: (e:MouseEvent<HTMLButtonElement>) => {},
            moreOptions: exportFileExtensions.map(ext => (
                {
                    label: ext.label,
                    action: async (e: MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        if (resultset && allPredictionResults.length > 0 && predictions.length > 0) {
                            try {
                                await downloadBatchPrediction(
                                    ext.file_type,
                                    resultset,
                                    allPredictionResults,
                                    threshold,
                                    predictions,
                                    evalMetadata,
                                    useProba,
                                );
                                setPopupMessage('You have successfully downloaded the selected resultset.', true);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading this resultset. Please try again.', false);
                            }
                        }
                    }
                }
            ))
        },
        {
            label: 'Delete',
            btnComp: (
                <DeleteModal 
                    items={[{
                        type: 'resultset',
                        id: id ? id : '',
                        name: resultset && resultset.name ? resultset.name : '',
                    }]}
                    deleteFn={deleteResultsets}
                />
            ),
        }
    ];
    
    const getResultsetRecords = (dataframe: { [key: string]: any }) => {
        const smiles = dataframe['structure/SMILES'] ?? [];
        return (smiles as string[]).length.toString();
    }
    

    const handleTabChange = (event: SyntheticEvent, index: number) => {
        setSelectedIndex(index);
    }

    
    return (
        <div className="p-10 h-full flex flex-col justify-start items-stretch gap-4">        
            <SinglePageHeader 
                id='single-resultset-header' 
                label={resultset ? resultset.name! : ''} 
                isLoading={isLoading} 
                pages={[
                    {
                        label: 'Resultsets',
                        action: (e: MouseEvent<HTMLButtonElement>) => {navigate('/resultsets')}
                    },
                    {
                        label: resultset ? resultset.name! : '',
                    }
                ]}
                itemMetadata={[{
                    label: 'Records',
                    value: resultsetDataframeV2 ? getResultsetRecords(resultsetDataframeV2) : '0',
                    adornment: <RecordsIcon/>,
                }]}
            >
                <NestedDropdown
                    dir="right"
                    btn={{
                        label: 'Options',
                        type: 'primary',
                        styling: 'solid',
                        id: 'single-file-options'
                    }}
                    options={dropdownOptions}
                />
                {resultsetMetadata.length > 0 && (
                    <UpdatedMetadataMenu 
                        menuLabel="Resultset Metadata"
                        menuItems={resultsetMetadata}
                        location="page"
                    />
                )}
            </SinglePageHeader>

            {predictions.length > 0 || isLoading ? (
            <>
                {pageTabs.length > 0 && (
                <div className="mb-4">
                    <NewPageTabs 
                        tabs={pageTabs.map((tab,i) => ({
                            label: tab,
                            index: i
                        }))}
                        selectedIndex={selectedIndex}
                        handleTabChange={handleTabChange}
                    />
                </div>
                )}

                {pageTabs.length === 0 || pageTabs[selectedIndex] === 'Prediction Scores' || pageTabs[selectedIndex] === 'Model Validations' || pageTabs[selectedIndex] === 'DCM Trust' || pageTabs[selectedIndex] === 'Explainability' ? (

                <>
                {modelFamilyOptions.length > 0 && (
                    <div className="max-w-[600px]">
                        <SelectField 
                            label="Model Family"
                            name="model_family"
                            id="model_family"
                            value={chosenModelFamily ? { value: chosenModelFamily.id, label: chosenModelFamily.name } : null}
                            options={modelFamilyOptions.map(option => ({ value: option.id, label: option.name }))}
                            isCreatable={false}
                            isRequired={false}
                            isMulti={false}
                            handleChange={handleChooseModelFamily}
                        />
                    </div>
                )}

                {pageTabs.length === 0 || pageTabs[selectedIndex] === 'Prediction Scores' ? (

                <>
                {isConformal && (
                    <ResultComparison 
                        data={predictionResults}
                        isLoading={isLoading} 
                        threshold={threshold}
                        setThreshold={setThreshold}
                        headers={headers}
                    />
                )}

                <div style={{height: 'calc(100vh - 70px)'}} className="mt-10 h-full overflow-hidden flex flex-col justify-start items-stretch gap-4">
                
                    <div className="w-full flex justify-between items-center gap-4">
                        <div className="flex justify-end items-center gap-4">
                            {isConformal && (
                            <HorizontalNumberSlider 
                                    label="Alpha Threshold"
                                    threshold={threshold}
                                    setThreshold={setThreshold}
                                    hasBorderAndPadding
                            />
                            )}
                        </div>
                        <div className="flex justify-end items-center gap-4">
                            {!isConformal && hasProba && (
                                <div className="min-w-[100px] pb-7">
                                    <BooleanField
                                        label="Use Probability"
                                        id="use-proba-toggle"
                                        name="useProba"
                                        value={useProba}
                                        isRequired={false}
                                        handleChange={(_, __) => setUseProba((prev) => !prev)}
                                        disabled={false}
                                    />
                                </div>
                            )}
                            {headers && <ColumnsButton 
                                headers={headers} 
                                setHeaders={setHeaders} 
                                isDisabled={false}
                            />}
                            <ListGridToggle setIsGrid={setIsGrid} isGrid={isGrid}/>
                        </div>
                    </div>

                    <>
                        {isGrid ? (
                            <>
                                <VirtualizedGridList 
                                    isLoading={isLoading} 
                                    gridData={paginatedItems}
                                    headers={headers}
                                    statsType="prediction"
                                    threshold={isConformal ? threshold : undefined}
                                    setPopupMessage={setPopupMessage}
                                />
                            </>
                        ) : (
                            <>
                                <VirtualizedDataTable 
                                    isLoading={isLoading} 
                                    headers={headers} 
                                    tableData={paginatedItems}
                                    statsType="prediction"
                                    threshold={isConformal ? threshold : undefined}
                                    setPopupMessage={setPopupMessage}
                                    moleculeHighlightingModelFamily={moleculeHighlightingModelFamily}
                                    fixedItemHeight={194}
                                />                  
                            </>
                        )}

                    </>

                    <Pagination
                        setItemsPerPage={setItemsPerPage}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                        startIndex={startIndex}
                        endIndex={endIndex}
                        totalPages={totalPages}
                        total={predictionResults.length}
                        paginate={paginate}
                    />
                </div>
                </>

                ) : pageTabs[selectedIndex] === 'Model Validations' ? (
                    <ModelValidations 
                        isLoading={isLoading}
                        evalMetadata={evalMetadata.filter(data => data.modelFamilyId === chosenModelFamily?.id)}
                        setPopupMessage={setPopupMessage}
                    />
                ) : pageTabs[selectedIndex] === 'DCM Trust' ? (
                    <DCMTrustTab
                        grouping={activeGrouping}
                        threshold={dcmThreshold}
                        setThreshold={setDcmThreshold}
                        selectedModelHash={selectedDcmModelHash}
                        setSelectedModelHash={setSelectedDcmModelHash}
                        modelHashLabels={modelHashLabels}
                    />
                ) : pageTabs[selectedIndex] === 'Explainability' ? (
                    <ResultsetExplainabilityTab
                        resultsetId={id ? Number(id) : 0}
                        resultsetDataframeV2={resultsetDataframeV2}
                        chosenModelFamilyId={chosenModelFamily ? chosenModelFamily.id : null}
                        modelHashLabels={modelHashLabels}
                    />
                ) : (
                    <></>
                )}

                </>

                ) : pageTabs[selectedIndex] === 'Visualizations' ? (
                    <ResultsetVisualizations 
                        setPopupMessage={setPopupMessage} 
                        resultset={resultset}
                        modelFamilyOptions={modelFamilyOptions}
                    />
                ) : (
                    <></>
                )}

            </>
            ) : (
                <EmptyRecords label="Resultsets"/>
            )}
            
            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  
            
    </div>
    )
}